"use client";

import React, { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import PageHero from "../../components/layout/PageHero";
import { errorDetail, useAuth } from "../../lib/auth";

const USERNAME_RE = /^[A-Za-z0-9_]{8,32}$/;
const inputCls =
  "w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500";
const labelCls = "block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5";
const btnCls =
  "w-full rounded-md bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2";

/** Only same-site paths are allowed as redirect targets. */
function safeNext(raw: string | null): string {
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/profile";
}

async function postJson(path: string, body: unknown) {
  return fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
}

function LoginContent() {
  const router = useRouter();
  const params = useSearchParams();
  const next = safeNext(params.get("next"));
  const { username: current, ready, setSession } = useAuth();

  const [lUser, setLUser] = useState("");
  const [lPass, setLPass] = useState("");
  const [lErr, setLErr] = useState("");
  const [lBusy, setLBusy] = useState(false);

  const [sUser, setSUser] = useState("");
  const [sPass, setSPass] = useState("");
  const [sPass2, setSPass2] = useState("");
  const [sErr, setSErr] = useState("");
  const [sBusy, setSBusy] = useState(false);

  // already logged in -> go straight on
  useEffect(() => {
    if (ready && current) router.replace(next);
  }, [ready, current, next, router]);

  const doLogin = async (username: string, password: string): Promise<string | null> => {
    const r = await postJson("/api/v2/auth/login", { username, password });
    if (!r.ok) return errorDetail(r, "Login failed");
    const body = await r.json();
    setSession(body.username ?? username, body.session_token);
    router.replace(next);
    return null;
  };

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLErr("");
    if (!lUser.trim() || !lPass) return setLErr("Enter your username and password.");
    setLBusy(true);
    try {
      const err = await doLogin(lUser.trim(), lPass);
      if (err) setLErr(err);
    } catch {
      setLErr("Could not reach the server. Try again.");
    } finally {
      setLBusy(false);
    }
  };

  const submitSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSErr("");
    const u = sUser.trim();
    if (!USERNAME_RE.test(u)) return setSErr("Username must be 8–32 characters: letters, digits or underscore.");
    if (sPass.length < 8) return setSErr("Password must be at least 8 characters.");
    if (sPass !== sPass2) return setSErr("The two passwords do not match.");
    setSBusy(true);
    try {
      const r = await postJson("/api/v2/auth/register", { username: u, password: sPass });
      if (!r.ok) {
        setSErr(r.status === 409 ? "That username is already taken." : await errorDetail(r, "Sign-up failed"));
        return;
      }
      const err = await doLogin(u, sPass); // register, then log in straight away
      if (err) setSErr(`Account created, but automatic login failed: ${err}`);
    } catch {
      setSErr("Could not reach the server. Try again.");
    } finally {
      setSBusy(false);
    }
  };

  return (
    <div className="py-6 px-4 md:px-8 font-sans">
      <div className="max-w-5xl mx-auto flex flex-col gap-8">
        <PageHero
          eyebrow="Account"
          title="Log in or sign up"
          subtitle="An account gives you a personal API token for scripts and a history of your searches. No email address is needed, and browsing the website never requires one."
        />

        <div className="grid md:grid-cols-2 gap-6">
          {/* Login */}
          <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" aria-labelledby="login-title">
            <h2 id="login-title" className="text-xl font-bold text-brand-900">Log in</h2>
            <p className="text-sm text-slate-500 mt-1 mb-5">Use your username and password to continue.</p>
            <form onSubmit={submitLogin} className="flex flex-col gap-4" noValidate>
              <div>
                <label htmlFor="login-user" className={labelCls}>Username</label>
                <input id="login-user" className={inputCls} autoComplete="username" value={lUser} onChange={(e) => setLUser(e.target.value)} />
              </div>
              <div>
                <label htmlFor="login-pass" className={labelCls}>Password</label>
                <input id="login-pass" type="password" className={inputCls} autoComplete="current-password" value={lPass} onChange={(e) => setLPass(e.target.value)} />
              </div>
              <div aria-live="polite" className="min-h-[1.25rem] text-sm text-red-700">{lErr}</div>
              <button type="submit" className={btnCls} disabled={lBusy}>{lBusy ? "Logging in…" : "Log in"}</button>
              <p className="text-xs text-slate-500">Having trouble? Check that caps lock is off.</p>
            </form>
          </section>

          {/* Sign up */}
          <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" aria-labelledby="signup-title">
            <h2 id="signup-title" className="text-xl font-bold text-brand-900">Sign up</h2>
            <p className="text-sm text-slate-500 mt-1 mb-5">Create a username and password. Your API token is generated for you.</p>
            <form onSubmit={submitSignup} className="flex flex-col gap-4" noValidate>
              <div>
                <label htmlFor="signup-user" className={labelCls}>Username</label>
                <input id="signup-user" className={inputCls} autoComplete="username" aria-describedby="signup-rules" value={sUser} onChange={(e) => setSUser(e.target.value)} />
              </div>
              <div>
                <label htmlFor="signup-pass" className={labelCls}>Password</label>
                <input id="signup-pass" type="password" className={inputCls} autoComplete="new-password" aria-describedby="signup-rules" value={sPass} onChange={(e) => setSPass(e.target.value)} />
              </div>
              <div>
                <label htmlFor="signup-pass2" className={labelCls}>Confirm password</label>
                <input id="signup-pass2" type="password" className={inputCls} autoComplete="new-password" value={sPass2} onChange={(e) => setSPass2(e.target.value)} />
              </div>
              <ul id="signup-rules" className="text-xs text-slate-500 list-disc pl-4 space-y-0.5">
                <li>Username: 8&ndash;32 characters, letters, digits or underscore.</li>
                <li>Password: at least 8 characters.</li>
              </ul>
              <div aria-live="polite" className="min-h-[1.25rem] text-sm text-red-700">{sErr}</div>
              <button type="submit" className={btnCls} disabled={sBusy}>{sBusy ? "Creating account…" : "Sign up"}</button>
            </form>
          </section>
        </div>

        <div className="rounded-lg border border-brand-100 bg-brand-50 px-5 py-4 text-sm text-brand-900 leading-relaxed">
          <strong>Why create an account?</strong> You get a personal API token for the REST API on the Integration page, a daily
          allowance of API calls, and a history of your searches and API requests on your profile page.
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="py-12 px-4 md:px-8 text-sm text-slate-500">Loading&hellip;</div>}>
      <LoginContent />
    </Suspense>
  );
}
